import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { App, Drawer, Form, Input, InputNumber, Modal, Popconfirm, Select } from 'antd';
import * as XLSX from 'xlsx';
import { listStudents, createStudent, updateStudent, deleteStudent, getStudentAssignmentsHistory } from '../../api/students';
import { listResidences, listMyAssignedResidences } from '../../api/residences';
import { listRooms, assignRoom, unassignRoom, bulkAssignRooms } from '../../api/rooms';
import { getApiErrorMessage } from '../../utils/apiError';
import TableCard, { AntPagination } from '../../components/TableCard';
import { SearchBar } from '../../components/ToolbarControls';
import { StudentStatusTag, EditIcon, DeleteIcon } from '../../components/UIKit';

const HomeIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>
  </svg>
);
const RemoveIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
  </svg>
);
const HistoryIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
  </svg>
);

export default function Students() {
  const { message, notification } = App.useApp();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tableSearch, setTableSearch] = useState('');
  const [filterGender, setFilterGender] = useState('');
  const [filterAge, setFilterAge] = useState(null);
  const [filterResidence, setFilterResidence] = useState('');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [residences, setResidences] = useState([]);
  const [form] = Form.useForm();

  const [assignOpen, setAssignOpen] = useState(false);
  const [assignStudent, setAssignStudent] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [roomsLoading, setRoomsLoading] = useState(false);
  const [roomSearch, setRoomSearch] = useState('');
  const [selectedRoomId, setSelectedRoomId] = useState(null);
  const [roomPage, setRoomPage] = useState(1);
  const roomPageSize = 8;
  const [assignForm] = Form.useForm();

  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [historyStudent, setHistoryStudent] = useState(null);
  const [historyPage, setHistoryPage] = useState(1);
  const historyPageSize = 8;

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [bulkAssignOpen, setBulkAssignOpen] = useState(false);
  const [bulkAssignLoading, setBulkAssignLoading] = useState(false);
  const [bulkResidenceId, setBulkResidenceId] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const students = await listStudents({});
      setRows(students || []);
      try {
        const res = await listResidences({});
        setResidences(res || []);
      } catch {
        const assignedRes = await listMyAssignedResidences().catch(() => []);
        setResidences(assignedRes || []);
      }
    } catch (e) {
      notification.error({ message: getApiErrorMessage(e, 'Impossible de charger la liste des étudiants.'), placement: 'topRight' });
    } finally { setLoading(false); }
  }, [notification]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const fetchRoomsForRes = useCallback(async (resId) => {
    if (!resId) { setRooms([]); return; }
    setRoomsLoading(true);
    try { const data = await listRooms({ residence_id: resId }); setRooms(data); }
    catch (e) { notification.error({ message: getApiErrorMessage(e, 'Impossible de charger les chambres.'), placement: 'topRight' }); }
    finally { setRoomsLoading(false); }
  }, [notification]);

  const openAssignRoom = useCallback(async (student) => {
    setAssignStudent(student);
    setAssignOpen(true);
    assignForm.resetFields();
    setSelectedRoomId(null);
    setRoomSearch('');
    setRooms([]);
    setRoomPage(1);
    
    const residenceId = student?.residence_id || student?.Residence?.id;
    if (residenceId) {
      assignForm.setFieldsValue({ _residence_id: residenceId });
      fetchRoomsForRes(residenceId);
    }
  }, [assignForm, fetchRoomsForRes]);

  const onAssignRoom = useCallback(async () => {
    try {
      const v = await assignForm.validateFields();
      await assignRoom({ student_id: assignStudent.id, room_id: v.room_id });
      message.success('Chambre affectée.');
      setAssignOpen(false); setAssignStudent(null); setSelectedRoomId(null); fetchAll();
    } catch (e) { notification.error({ message: getApiErrorMessage(e, 'Affectation échouée.'), placement: 'topRight' }); }
  }, [assignForm, assignStudent, fetchAll, message, notification]);

  const onUnassignRoom = useCallback(async (record) => {
    try {
      await unassignRoom({ student_id: record.id });
      message.success('Chambre retirée.');
      fetchAll();
    } catch (e) {
      notification.error({ message: getApiErrorMessage(e, 'Désassignation échouée.'), placement: 'topRight' });
    }
  }, [fetchAll, message, notification]);

  const onOpenHistory = useCallback(async (student) => {
    setHistoryStudent(student);
    setHistoryOpen(true);
    setHistoryLoading(true);
    setHistoryPage(1);
    try {
      const data = await getStudentAssignmentsHistory(student.id);
      setHistoryData(data || []);
    } catch (e) {
      notification.error({ message: getApiErrorMessage(e, 'Impossible de charger l\'historique.'), placement: 'topRight' });
    } finally {
      setHistoryLoading(false);
    }
  }, [notification]);

  const onSelectAll = (e) => {
    if (e.target.checked) setSelectedRowKeys(filteredRows.map(r => r.id));
    else setSelectedRowKeys([]);
  };

  const onSelectRow = (id) => {
    setSelectedRowKeys(prev => prev.includes(id) ? prev.filter(k => k !== id) : [...prev, id]);
  };

  const onBulkAssign = async () => {
    if (!bulkResidenceId) {
      message.error('Veuillez sélectionner une résidence.');
      return;
    }
    setBulkAssignLoading(true);
    try {
      const resp = await bulkAssignRooms({
        student_ids: selectedRowKeys,
        residence_id: bulkResidenceId
      });
      const { assigned, failed } = resp;
      if (failed && failed.length > 0) {
        notification.warning({
          message: 'Affectation partielle',
          description: `${assigned?.length} affectés, ${failed?.length} échoués (Erreur de mixité ou plus de place).`,
          placement: 'topRight',
          duration: 8
        });
      } else {
        message.success(`${assigned?.length} étudiants affectés avec succès.`);
      }
      setBulkAssignOpen(false);
      setSelectedRowKeys([]);
      setBulkResidenceId(null);
      fetchAll();
    } catch (e) {
      notification.error({ message: getApiErrorMessage(e, 'Affectation en masse échouée.'), placement: 'topRight' });
    } finally {
      setBulkAssignLoading(false);
    }
  };

  const roomRows = useMemo(() => {
    const q = String(roomSearch || '').trim().toLowerCase();
    const base = Array.isArray(rooms) ? rooms : [];
    if (!q) return base;
    return base.filter((r) => String(r?.code || '').toLowerCase().includes(q) || String(r?.notes || '').toLowerCase().includes(q));
  }, [roomSearch, rooms]);

  const pagedRoomRows = useMemo(() => {
    const start = (roomPage - 1) * roomPageSize;
    return roomRows.slice(start, start + roomPageSize);
  }, [roomPage, roomRows]);

  const pagedHistoryRows = useMemo(() => {
    const start = (historyPage - 1) * historyPageSize;
    return historyData.slice(start, start + historyPageSize);
  }, [historyData, historyPage]);

  const onEdit = useCallback((record) => {
    setEditing(record);
    form.setFieldsValue({ student_code: record.student_code, age: record.age, parent_phone: record.parent_phone, residence_id: record.residence_id });
    setOpen(true);
  }, [form]);

  const onDeleteRow = useCallback(async (record) => {
    try { await deleteStudent(record.id); message.success('Étudiant supprimé.'); fetchAll(); }
    catch (e) { notification.error({ message: getApiErrorMessage(e, 'Suppression échouée.'), placement: 'topRight' }); }
  }, [fetchAll, message, notification]);

  const onSubmit = async () => {
    try {
      const values = await form.validateFields();
      const { confirm_password, ...payload } = values;
      if (editing) { await updateStudent(editing.id, payload); message.success('Étudiant modifié.'); }
      else { await createStudent(payload); message.success('Étudiant créé.'); }
      setOpen(false); fetchAll();
    } catch (e) {
      if (e?.response || e?.message) notification.error({ message: getApiErrorMessage(e, 'Enregistrement échoué.'), placement: 'topRight' });
    }
  };

  const filteredRows = useMemo(() => {
    const q = String(tableSearch || '').trim().toLowerCase();
    const g = String(filterGender || '').trim().toLowerCase();
    const a = filterAge;
    const rFilter = String(filterResidence || '').trim();
    const base = Array.isArray(rows) ? rows : [];
    return base.filter((s) => {
      // Basic search
      let matchSearch = true;
      if (q) {
        const code = String(s?.student_code || '').toLowerCase();
        const prenom = String(s?.User?.prenom || '').toLowerCase();
        const nom = String(s?.User?.nom || '').toLowerCase();
        const email = String(s?.User?.email || '').toLowerCase();
        const residence = String(s?.Residence?.nom || '').toLowerCase();
        const chambre = String(s?.chambre || s?.RoomAssignments?.[0]?.Room?.code || '').toLowerCase();
        matchSearch = code.includes(q) || prenom.includes(q) || nom.includes(q) || email.includes(q) || residence.includes(q) || chambre.includes(q);
      }
      // Gender filter
      let matchGender = true;
      if (g) {
        const sg = String(s?.gender || '').toLowerCase();
        matchGender = sg.startsWith(g) || (g === 'm' && (sg === 'h' || sg === 'homme')) || (g === 'f' && (sg === 'femme'));
      }
      // Age filter
      let matchAge = true;
      if (a !== null && a !== undefined) {
        matchAge = Number(s?.age) === Number(a);
      }
      // Residence filter
      let matchResidence = true;
      if (rFilter) {
        if (rFilter === '__unassigned__') {
          matchResidence = !s?.residence_id;
        } else {
          matchResidence = String(s?.residence_id) === String(rFilter);
        }
      }

      return matchSearch && matchGender && matchAge && matchResidence;
    });
  }, [rows, tableSearch, filterGender, filterAge, filterResidence]);

  const onExportExcel = useCallback(() => {
    if (!filteredRows || filteredRows.length === 0) {
      message.warning('Aucune donnée à exporter.');
      return;
    }
    const data = filteredRows.map(r => ({
      'ID Étudiant': r.student_code || '-',
      'Nom': r.User?.nom || '-',
      'Prénom': r.User?.prenom || '-',
      'Email': r.User?.email || '-',
      'Genre': r.gender || '-',
      'Âge': r.age ?? '-',
      'Tél. parent': r.parent_phone || '-',
      'Résidence': r.Residence?.nom || '-',
      'Chambre': r.RoomAssignments?.[0]?.Room?.code || r.chambre || '-',
      'Statut': r.statut || '-'
    }));
    
    const ws = XLSX.utils.json_to_sheet(data);
    
    // Set column widths
    ws['!cols'] = [
      { wch: 15 }, // ID
      { wch: 20 }, // Nom
      { wch: 20 }, // Prénom
      { wch: 30 }, // Email
      { wch: 10 }, // Genre
      { wch: 8 },  // Age
      { wch: 18 }, // Tel parent
      { wch: 20 }, // Residence
      { wch: 12 }, // Chambre
      { wch: 15 }  // Statut
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Étudiants");
    XLSX.writeFile(wb, `Etudiants_${new Date().toISOString().split('T')[0]}.xlsx`);
  }, [filteredRows, message]);

  return (
    <div className="page">
      <TableCard
        label="Étudiants"
        loading={loading}
        rows={filteredRows}
        toolbar={
          <div style={{ display: 'flex', gap: 8 }}>
            {selectedRowKeys.length > 0 && (
              <button className="btn-primary" style={{ backgroundColor: '#1890ff', borderColor: '#1890ff' }} onClick={() => setBulkAssignOpen(true)}>
                Affecter ({selectedRowKeys.length})
              </button>
            )}
            <button className="btn-outline" onClick={onExportExcel}>Exporter (Excel)</button>
          </div>
        }
        search={
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <div style={{ flex: 1, minWidth: 200 }}><SearchBar value={tableSearch} onChange={setTableSearch} placeholder="ID, nom, email, résidence..." /></div>
            <Select 
              allowClear 
              placeholder="Résidence" 
              value={filterResidence || undefined} 
              onChange={setFilterResidence} 
              options={[{ value: '__unassigned__', label: 'Aucune (Non assigné)' }, ...residences.map(r => ({ value: r.id, label: r.nom }))]} 
              style={{ width: 170 }} 
            />
            <Select allowClear placeholder="Sexe / Genre" value={filterGender || undefined} onChange={setFilterGender} options={[{ value: 'M', label: 'Homme (M)' }, { value: 'F', label: 'Femme (F)' }]} style={{ width: 140 }} />
            <InputNumber allowClear placeholder="Âge" value={filterAge} onChange={setFilterAge} min={0} style={{ width: 90 }} />
          </div>
        }
      >
        {(pageRows) => (
        <table>
          <thead>
            <tr>
              <th style={{ width: 40 }}><input type="checkbox" checked={filteredRows.length > 0 && selectedRowKeys.length === filteredRows.length} onChange={onSelectAll} /></th>
              <th>ID étudiant</th>
              <th>Nom</th>
              <th>Prénom</th>
              <th>Email</th>
              <th>Genre</th>
              <th>Âge</th>
              <th>Tél. parent</th>
              <th>Résidence</th>
              <th>Chambre</th>
              <th>Statut</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {pageRows.map((r) => (
              <tr key={r.id}>
                <td onClick={(e) => e.stopPropagation()}><input type="checkbox" checked={selectedRowKeys.includes(r.id)} onChange={() => onSelectRow(r.id)} /></td>
                <td>{r.student_code || '-'}</td>
                <td>{r.User?.nom || '-'}</td>
                <td>{r.User?.prenom || '-'}</td>
                <td>{r.User?.email || '-'}</td>
                <td>{r.gender || '-'}</td>
                <td>{r.age ?? '-'}</td>
                <td>{r.parent_phone || '-'}</td>
                <td>{r.Residence?.nom || '-'}</td>
                <td>{r.RoomAssignments?.[0]?.Room?.code || r.chambre || '-'}</td>
                <td>{r.statut ? <StudentStatusTag status={r.statut} /> : '-'}</td>
                <td>
                  <div className="actions-cell">
                    <button className="action-btn" title="Historique" onClick={() => onOpenHistory(r)}><HistoryIcon /></button>
                    <button className="action-btn" title="Modifier" onClick={() => onEdit(r)}><EditIcon /></button>
                    <button className="action-btn" title="Affecter chambre" onClick={() => openAssignRoom(r)}><HomeIcon /></button>
                    {r.RoomAssignments?.[0]?.Room?.code && (
                      <Popconfirm title="Retirer la chambre ?" description="L'étudiant n'aura plus de résidence assignée." okText="Retirer" cancelText="Annuler" onConfirm={() => onUnassignRoom(r)}>
                        <button className="action-btn" title="Désassigner"><RemoveIcon /></button>
                      </Popconfirm>
                    )}
                    <Popconfirm title="Supprimer cet étudiant ?" description={r?.User?.email} okText="Supprimer" cancelText="Annuler" okType="danger" onConfirm={() => onDeleteRow(r)}>
                      <button className="action-btn danger" title="Supprimer"><DeleteIcon /></button>
                    </Popconfirm>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        )}
      </TableCard>

      {/* Form Drawer */}
      <Drawer
        title={editing ? 'Modifier étudiant' : 'Nouvel étudiant'}
        placement="right"
        width={480}
        onClose={() => setOpen(false)}
        open={open}
        destroyOnClose
        extra={
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn-outline" onClick={() => setOpen(false)}>Annuler</button>
            <button className="btn-primary" onClick={onSubmit}>{editing ? 'Enregistrer' : 'Créer'}</button>
          </div>
        }
      >
        <Form form={form} layout="vertical">
          {!editing && (<>
            <Form.Item name="nom" label="Nom" rules={[{ required: true }]}><Input /></Form.Item>
            <Form.Item name="prenom" label="Prénom" rules={[{ required: true }]}><Input /></Form.Item>
            <Form.Item name="student_code" label="ID étudiant" rules={[{ required: true }]}><Input placeholder="Ex: 107205Me" /></Form.Item>
            <Form.Item name="email" label="Email" rules={[{ required: true, type: 'email' }]}><Input /></Form.Item>
            <Form.Item name="password" label="Mot de passe" rules={[{ required: true, min: 8 }]}><Input.Password /></Form.Item>
            <Form.Item name="confirm_password" label="Confirmer le mot de passe" dependencies={['password']} rules={[{ required: true }, ({ getFieldValue }) => ({ validator(_, value) { if (!value || getFieldValue('password') === value) return Promise.resolve(); return Promise.reject(new Error('Les mots de passe ne correspondent pas')); } })]}>
              <Input.Password />
            </Form.Item>
          </>)}
          {editing && <Form.Item name="student_code" label="ID étudiant" rules={[{ required: true }]}><Input /></Form.Item>}
          <Form.Item name="age" label="Âge"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="parent_phone" label="Téléphone parent"><Input /></Form.Item>
          <Form.Item name="residence_id" label="Résidence" rules={[{ required: true }]}>
            <Select options={residences.map(r => ({ value: r.id, label: r.nom }))} />
          </Form.Item>
        </Form>
      </Drawer>

      {/* Assign Room Modal */}
      <Modal
        title={assignStudent ? `Affecter une chambre — ${assignStudent?.User?.prenom || ''} ${assignStudent?.User?.nom || ''}` : 'Affecter une chambre'}
        open={assignOpen}
        onCancel={() => { setAssignOpen(false); setAssignStudent(null); setSelectedRoomId(null); setRoomSearch(''); assignForm.resetFields(); }}
        onOk={onAssignRoom}
        okText="Affecter"
        confirmLoading={roomsLoading}
      >
        <Form form={assignForm} layout="vertical">
          <Form.Item name="room_id" rules={[{ required: true, message: 'Sélectionnez une chambre' }]} hidden><Input /></Form.Item>
          <Form.Item name="_residence_id" label="Résidence (Filtre)" rules={[{ required: true, message: 'Sélectionnez d\'abord une résidence' }]}>
            <Select 
              placeholder="Choisir une résidence"
              options={residences.map(r => ({ value: r.id, label: r.nom }))}
              onChange={(val) => {
                assignForm.setFieldsValue({ room_id: null });
                setSelectedRoomId(null);
                fetchRoomsForRes(val);
              }}
            />
          </Form.Item>
          <SearchBar value={roomSearch} onChange={setRoomSearch} placeholder={roomsLoading ? 'Chargement...' : 'Rechercher une chambre (code, notes)'} />
          <div className="table-card" style={{ marginTop: 14 }}>
            <div className="table-scroll">
              <table>
                <thead><tr><th>Chambre</th><th>Capacité</th><th>Occupés</th><th>Places libres</th></tr></thead>
                <tbody>
                  {pagedRoomRows.map((r) => {
                    const cap = Number(r?.capacity ?? 1);
                    const occ = Number(r?.active_occupants ?? 0);
                    const free = Math.max(0, cap - occ);
                    const full = cap > 0 && occ >= cap;
                    return (
                      <tr key={r.id} onClick={() => { if (!full) { setSelectedRoomId(r.id); assignForm.setFieldsValue({ room_id: r.id }); } }} style={{ cursor: full ? 'not-allowed' : 'pointer', opacity: full ? 0.5 : 1, background: selectedRoomId === r.id ? 'rgba(0,0,0,0.04)' : undefined }}>
                        <td>{r.code}</td>
                        <td>{cap}</td>
                        <td>{occ}</td>
                        <td>{free}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          <div style={{ marginTop: 8 }}>
            <AntPagination current={roomPage} total={roomRows.length} pageSize={roomPageSize} onChange={setRoomPage} />
          </div>
          <p style={{ marginTop: 8, fontSize: 11, color: '#888' }}>Si la chambre n'existe pas, créez-la depuis la page « Chambres ».</p>
        </Form>
      </Modal>

      {/* History Modal */}
      <Modal
        title={`Historique des Chambres — ${historyStudent?.User?.prenom || ''} ${historyStudent?.User?.nom || ''}`}
        open={historyOpen}
        onCancel={() => { setHistoryOpen(false); setHistoryStudent(null); setHistoryData([]); }}
        footer={null}
        width={600}
      >
        <div className="table-card" style={{ marginTop: 16 }}>
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Résidence</th>
                  <th>Chambre</th>
                  <th>Date d'entrée</th>
                  <th>Date de sortie</th>
                </tr>
              </thead>
              <tbody>
                {historyLoading ? (
                  <tr><td colSpan={4} style={{ textAlign: 'center', padding: 20 }}>Chargement...</td></tr>
                ) : historyData.length === 0 ? (
                  <tr><td colSpan={4} style={{ textAlign: 'center', padding: 20 }}>Aucun historique trouvé.</td></tr>
                ) : (
                  pagedHistoryRows.map((a, i) => (
                    <tr key={a.id} style={{ opacity: a.end_date ? 0.7 : 1, fontWeight: !a.end_date ? 'bold' : 'normal' }}>
                      <td>{a.Room?.Residence?.nom || '-'}</td>
                      <td>{a.Room?.code || '-'} {a.Room?.Building?.nom ? `(${a.Room.Building.nom})` : ''}</td>
                      <td>{a.start_date ? new Date(a.start_date).toLocaleDateString() : '-'}</td>
                      <td>{a.end_date ? new Date(a.end_date).toLocaleDateString() : <span style={{ color: 'green' }}>Actuelle</span>}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
        {!historyLoading && (
          <div style={{ marginTop: 8 }}>
            <AntPagination current={historyPage} total={historyData.length} pageSize={historyPageSize} onChange={setHistoryPage} />
          </div>
        )}
      </Modal>

      {/* Bulk Assign Modal */}
      <Modal
        title={`Affectation en masse (${selectedRowKeys.length} étudiants)`}
        open={bulkAssignOpen}
        onCancel={() => { setBulkAssignOpen(false); setBulkResidenceId(null); }}
        onOk={onBulkAssign}
        confirmLoading={bulkAssignLoading}
        okText="Lancer l'affectation"
      >
        <div style={{ marginBottom: 16 }}>
          Veuillez choisir la résidence cible. Le système se chargera de répartir automatiquement les étudiants dans les chambres disponibles, en respectant la mixité (F/H) et la capacité des chambres.
        </div>
        <Select 
          style={{ width: '100%' }}
          placeholder="Choisir la résidence cible"
          value={bulkResidenceId}
          onChange={setBulkResidenceId}
          options={residences.map(r => ({ value: r.id, label: r.nom }))}
        />
      </Modal>
    </div>
  );
}

